"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import type { FormattedError } from "../types";

interface ErrorCardProps {
  fieldName: string;
  error: unknown;
}

function isFormattedError(value: unknown): value is FormattedError {
  return (
    typeof value === "object" &&
    value !== null &&
    ("message" in value || "type" in value)
  );
}

export function ErrorCard({ fieldName, error }: ErrorCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Extract error details safely
  const errorObj = isFormattedError(error) ? error : null;
  const errorType = errorObj?.type || "Unknown";
  const errorMessage = errorObj?.message || String(error);
  const errorCode = errorObj?.code;
  const errorRetryable = errorObj?.retryable;
  const errorStack = errorObj?.stack;
  const errorTimestamp = errorObj?.timestamp;

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
      {/* Header */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        className="p-4 cursor-pointer hover:bg-red-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono font-semibold text-red-900">
                {fieldName}
              </span>
              {errorCode && (
                <span className="text-xs font-medium text-red-700 bg-red-200 px-2 py-0.5 rounded">
                  {errorCode}
                </span>
              )}
              {errorRetryable !== undefined && (
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded ${
                    errorRetryable
                      ? "text-orange-700 bg-orange-200"
                      : "text-red-700 bg-red-300"
                  }`}
                >
                  {errorRetryable ? "Retryable" : "Not Retryable"}
                </span>
              )}
            </div>
            <div className="text-sm text-red-800 mb-1">{errorMessage}</div>
            {errorTimestamp && (
              <div className="text-xs text-red-600">
                {new Date(errorTimestamp).toLocaleString()}
              </div>
            )}
          </div>
          <div className="flex-shrink-0 ml-4">
            {isExpanded ? (
              <ChevronDown className="h-5 w-5 text-red-600" />
            ) : (
              <ChevronRight className="h-5 w-5 text-red-600" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-red-200 bg-white p-4 space-y-3">
          {/* Error Type */}
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1">Type</div>
            <div className="text-xs font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
              {errorType}
            </div>
          </div>

          {/* Stack Trace */}
          {errorStack && (
            <div>
              <div className="text-xs font-semibold text-gray-700 mb-1">
                Stack Trace
              </div>
              <pre className="text-xs font-mono text-gray-800 bg-gray-100 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                {errorStack}
              </pre>
            </div>
          )}

          {/* Full Error Object */}
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1">
              Full Error Object
            </div>
            <pre className="text-xs font-mono text-gray-800 bg-gray-100 p-2 rounded overflow-x-auto max-h-60 overflow-y-auto">
              {JSON.stringify(error, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
